import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  Stack,
  Grid,
  Paper
} from '@mui/material';
import CanvasJSReact from '@canvasjs/react-charts';
import { AxiosInstanceLongTimeOut } from '@service/axios-instance';

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

interface ImuChartModalProps {
  open: boolean;
  onClose: () => void;
  tid: string | null;
  stamp: string | null;
  db: string;
  eventData?: any;
}

const customMarkerIcon = new L.DivIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="#ea4335" style="filter: drop-shadow(0px 2px 2px rgba(0,0,0,0.4));"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>`,
  className: '',
  iconSize: [40, 40],
  iconAnchor: [20, 40],
});

const formatBRLTime = (stampStr: string) => {
  if (!stampStr) return '-';
  try {
    const brDateMatch = stampStr.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[,\s]+(.*))?/);
    if (brDateMatch) {
      const [_, d, m, y, time] = brDateMatch;
      const shortYear = y.slice(2);
      const shortTime = time ? time.split(':').slice(0, 2).join(':') : '00:00';
      return `${d}/${m}/${shortYear} ${shortTime}`;
    }
    const dateStr = stampStr.includes('GMT') || stampStr.endsWith('Z') ? stampStr : stampStr + 'Z';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return stampStr;
    const brlDate = new Date(d.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
    const day = String(brlDate.getDate()).padStart(2, '0');
    const month = String(brlDate.getMonth() + 1).padStart(2, '0');
    const year = String(brlDate.getFullYear()).slice(2);
    const hours = String(brlDate.getHours()).padStart(2, '0');
    const mins = String(brlDate.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch (e) {
    return stampStr;
  }
};

export default function ImuChartModal({ open, onClose, tid, stamp, db, eventData }: ImuChartModalProps) {
  const [chartOptions, setChartOptions] = useState<any>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await AxiosInstanceLongTimeOut.get(`/events/get_imu_recording?tid=${tid}&stamp=${stamp}&db=${db}`, {
        responseType: 'arraybuffer',
        withCredentials: true
      });

      const extractedFileName = res.headers['x-file-name'] || res.headers['X-File-Name'] || 'Binário';
      setFileName(extractedFileName);

      const buffer = res.data;
      const dataView = new DataView(buffer);

      const ACC_SCALE = (1.0 / 4096.0) * 9.81;
      const GYRO_SCALE = (1.0 / 65.5) * (Math.PI / 180.0);

      const N = Math.floor(buffer.byteLength / 12);
      const dpsAccX: any[] = [], dpsAccY: any[] = [], dpsAccZ: any[] = [];
      const dpsMag: any[] = [];
      const dpsGyroX: any[] = [], dpsGyroY: any[] = [], dpsGyroZ: any[] = [];

      const dpsAccVert: any[] = [];
      const dpsAccHoriz: any[] = [];
      const dpsJerkH: any[] = [];
      const crashMarkers: any[] = [];
      const holeMarkers: any[] = [];

      let maxMag = 0;
      let sumMag = 0;
      let sumSqMag = 0;
      const magArray = new Float32Array(N);

      const kGravity = 9.81;
      const kSampleRateHz = 400;
      const kWarmupAlpha = 0.08;
      const kRunAlpha = 0.005;
      const g_est = [0.0, 0.0, 1.0];
      let stable_samples = 0;
      let armed = false;
      let prev_acc_horiz = 0;
      let hole_candidate = false;
      let hole_timer = 0;
      let last_event_idx = -10000;
      const kDebounceEventSamples = 5000 * 400 / 1000; // 5000ms

      const p_crash_acc = 2.0 * kGravity;
      const p_crash_jerk = 500.0;
      const p_hole_drop = 0.4 * kGravity;
      const p_hole_impact = 2.0 * kGravity;

      for (let i = 0; i < N; i++) {
        const offset = i * 12;
        const accX = dataView.getInt16(offset, false) * ACC_SCALE;
        const accY = dataView.getInt16(offset + 2, false) * ACC_SCALE;
        const accZ = dataView.getInt16(offset + 4, false) * ACC_SCALE;
        const gyroX = dataView.getInt16(offset + 6, false) * GYRO_SCALE;
        const gyroY = dataView.getInt16(offset + 8, false) * GYRO_SCALE;
        const gyroZ = dataView.getInt16(offset + 10, false) * GYRO_SCALE;

        const mag = Math.sqrt(accX * accX + accY * accY + accZ * accZ);
        if (mag > maxMag) maxMag = mag;
        sumMag += mag;
        sumSqMag += mag * mag;
        magArray[i] = mag;

        // Event Filter Simulation
        const stable_sample = mag > (0.5 * kGravity) && mag < (1.5 * kGravity);
        const was_armed = armed;

        if (stable_sample) {
          const alpha = was_armed ? kRunAlpha : kWarmupAlpha;
          g_est[0] = g_est[0] * (1.0 - alpha) + accX * alpha;
          g_est[1] = g_est[1] * (1.0 - alpha) + accY * alpha;
          g_est[2] = g_est[2] * (1.0 - alpha) + accZ * alpha;
          if (!was_armed) {
            stable_samples++;
            if (stable_samples >= 100) armed = true;
          }
        } else if (!was_armed) {
          stable_samples = 0;
          hole_candidate = false;
        }

        const g_len = Math.sqrt(g_est[0] * g_est[0] + g_est[1] * g_est[1] + g_est[2] * g_est[2]);
        let g_norm = [0, 0, 1];
        if (g_len > 0.001) {
          g_norm = [g_est[0] / g_len, g_est[1] / g_len, g_est[2] / g_len];
        }

        const acc_vert = accX * g_norm[0] + accY * g_norm[1] + accZ * g_norm[2];
        let acc_horiz_sq = (mag * mag) - (acc_vert * acc_vert);
        if (acc_horiz_sq < 0) acc_horiz_sq = 0;
        const acc_horiz = Math.sqrt(acc_horiz_sq);

        let jerk_h = 0;

        if (armed) {
          if (acc_horiz > p_crash_acc) {
            jerk_h = (acc_horiz - prev_acc_horiz) * kSampleRateHz;
            prev_acc_horiz = acc_horiz;

            if (Math.abs(jerk_h) > p_crash_jerk) {
              if (i - last_event_idx > kDebounceEventSamples) {
                crashMarkers.push({ x: i, y: acc_horiz, indexLabel: "CRASH", markerColor: "red", markerType: "cross" });
                last_event_idx = i;
                hole_candidate = false;
              }
            }
          } else {
            prev_acc_horiz = acc_horiz;
          }

          if (mag < p_hole_drop) {
            hole_candidate = true;
            hole_timer = i;
          }

          if (hole_candidate) {
            if (i - hole_timer > (200 * 400 / 1000)) {
              hole_candidate = false;
            } else if (acc_vert > p_hole_impact) {
              if (i - last_event_idx > kDebounceEventSamples) {
                holeMarkers.push({ x: i, y: acc_vert, indexLabel: "HOLE", markerColor: "orange", markerType: "triangle" });
                last_event_idx = i;
              }
            }
          }
        }

        dpsAccX.push({ x: i, y: accX });
        dpsAccY.push({ x: i, y: accY });
        dpsAccZ.push({ x: i, y: accZ });
        dpsMag.push({ x: i, y: mag });
        dpsGyroX.push({ x: i, y: gyroX });
        dpsGyroY.push({ x: i, y: gyroY });
        dpsGyroZ.push({ x: i, y: gyroZ });
        dpsAccVert.push({ x: i, y: acc_vert });
        dpsAccHoriz.push({ x: i, y: acc_horiz });
        dpsJerkH.push({ x: i, y: jerk_h });
      }

      const meanMag = sumMag / N;
      const variance = (sumSqMag / N) - (meanMag * meanMag);
      const stdDev = Math.sqrt(variance);
      const rms = Math.sqrt(sumSqMag / N);
      const crestFactor = maxMag / rms;

      let sumFourth = 0;
      for (let i = 0; i < N; i++) {
        sumFourth += Math.pow(magArray[i] - meanMag, 4);
      }
      const kurtosis = (sumFourth / N) / Math.pow(variance, 2);

      setMetrics({ maxMag, meanMag, stdDev, rms, crestFactor, kurtosis });

      setChartOptions({
        acc: {
          animationEnabled: true,
          zoomEnabled: true,
          title: { text: "Acelerômetro (m/s²)", fontSize: 16 },
          axisX: { title: "Amostras" },
          toolTip: { shared: true },
          data: [
            { type: "line", name: "X", showInLegend: true, dataPoints: dpsAccX, color: "#e6194b" },
            { type: "line", name: "Y", showInLegend: true, dataPoints: dpsAccY, color: "#3cb44b" },
            { type: "line", name: "Z", showInLegend: true, dataPoints: dpsAccZ, color: "#4363d8" }
          ]
        },
        magHist: {
          animationEnabled: true,
          title: { text: "Distribuição de Magnitude", fontSize: 16 },
          axisX: { title: "Magnitude (m/s²)" },
          axisY: { title: "Ocorrências" },
          data: [{
            type: "column",
            dataPoints: createHistogram(magArray, 20)
          }]
        },
        gyro: {
          animationEnabled: true,
          zoomEnabled: true,
          title: { text: "Giroscópio (rad/s)", fontSize: 16 },
          axisX: { title: "Amostras" },
          toolTip: { shared: true },
          data: [
            { type: "line", name: "X", showInLegend: true, dataPoints: dpsGyroX, color: "#f58231" },
            { type: "line", name: "Y", showInLegend: true, dataPoints: dpsGyroY, color: "#f032e6" },
            { type: "line", name: "Z", showInLegend: true, dataPoints: dpsGyroZ, color: "#800000" }
          ]
        },
        telemetry: {
          animationEnabled: true,
          zoomEnabled: true,
          title: { text: "Filtro de Eventos (Telemetria)", fontSize: 16 },
          axisX: { title: "Amostras" },
          axisY: { title: "Aceleração (m/s²)" },
          axisY2: { title: "Jerk H (m/s³)" },
          toolTip: { shared: true },
          data: [
            { type: "line", name: "Acc Horiz (m/s²)", showInLegend: true, dataPoints: dpsAccHoriz, color: "#1f77b4" },
            { type: "line", name: "Acc Vert (m/s²)", showInLegend: true, dataPoints: dpsAccVert, color: "#ff7f0e" },
            { type: "line", name: "Jerk H (m/s³)", showInLegend: true, dataPoints: dpsJerkH, color: "#2ca02c", axisYType: "secondary" },
            { type: "scatter", name: "Crash", showInLegend: true, dataPoints: crashMarkers, color: "red", markerSize: 15 },
            { type: "scatter", name: "Hole", showInLegend: true, dataPoints: holeMarkers, color: "orange", markerSize: 15 }
          ]
        }
      });
    } catch (err: any) {
      if (err.response && err.response.status === 404) {
        setError('Gráficos indisponíveis');
      } else {
        setError(err.message || 'Erro ao carregar dados do IMU.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && tid && stamp && db) {
      loadData();
    }
  }, [open, tid, stamp, db]);

  const eventTypeStr = eventData?.type?.toLowerCase() || '';
  const isCrash = eventTypeStr.includes('crash');
  const isHole = eventTypeStr.includes('hole');
  const showAccH = !isHole;
  const showAX = !isHole;
  const showAY = !isHole;
  const showSVM = !isCrash;
  const showTranco = !isHole;
  const metricsCount = (showAccH ? 1 : 0) + 1 + (showAX ? 1 : 0) + (showAY ? 1 : 0) + (showSVM ? 1 : 0) + (showTranco ? 1 : 0);
  const printBasis = (100 / metricsCount) + '%';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth sx={{ '@media print': { '& .MuiDialog-paper': { m: 0, p: 0, boxShadow: 'none', maxWidth: 'none', width: '100%', height: 'auto', overflow: 'visible' } } }}>
      <style type="text/css" media="print">
        {`
          body > *:not([role="presentation"]) {
            display: none !important;
          }
          body {
            background-color: white !important;
          }
          .MuiBackdrop-root {
            display: none !important;
          }
          .MuiDialog-container {
            height: auto !important;
            display: block !important;
            position: relative !important;
          }
          @page {
            size: A4;
            margin: 1cm;
          }
        `}
      </style>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', '@media print': { display: 'none' } }}>
        <Typography variant="h6">Gravação IMU (Blackbox)</Typography>
        <Box>
          <Button variant="outlined" size="small" onClick={() => window.print()} sx={{ mr: 1 }}>Baixar PDF</Button>
          <Button variant="contained" size="small" component="a" href={`/events/get_imu_recording?tid=${tid}&stamp=${stamp}&db=${db}`} download>Baixar Binário</Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        )}
        {!loading && eventData && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="h6" gutterBottom color="primary">Relatório do Evento</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3} sx={{ '@media print': { flexBasis: '25%', maxWidth: '25%' } }}>
                <Typography variant="caption" color="text.secondary">Motorista / Usuário</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {([eventData.username, eventData.user_name].find(name => typeof name === 'string' && name.trim() && name.trim() !== '—')) || (eventData.user == 0 ? 'Autologin' : (eventData.user || 'Desconhecido'))}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ '@media print': { flexBasis: '25%', maxWidth: '25%' } }}>
                <Typography variant="caption" color="text.secondary">Horário</Typography>
                <Typography variant="body1" fontWeight="bold">{formatBRLTime(eventData.stamp)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ '@media print': { flexBasis: '25%', maxWidth: '25%' } }}>
                <Typography variant="caption" color="text.secondary">Tipo de Evento</Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>{eventData.type}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ '@media print': { flexBasis: '25%', maxWidth: '25%' } }}>
                <Typography variant="caption" color="text.secondary">Setor / Máquina</Typography>
                <Typography variant="body1" fontWeight="bold">{eventData.setor || '-'} / {eventData.tid}</Typography>
              </Grid>

              {/* Extra Event Metrics */}
              {showAccH && (
                <Grid item xs={4} sm={2} md={2} sx={{ '@media print': { flexBasis: printBasis, maxWidth: printBasis } }}>
                  <Typography variant="caption" color="text.secondary">Acc H</Typography>
                  <Typography variant="body1" fontWeight="bold">{eventData.acc_h != null ? Number(eventData.acc_h).toFixed(2) : '-'}</Typography>
                </Grid>
              )}
              <Grid item xs={4} sm={2} md={2} sx={{ '@media print': { flexBasis: printBasis, maxWidth: printBasis } }}>
                <Typography variant="caption" color="text.secondary">Acc V</Typography>
                <Typography variant="body1" fontWeight="bold">{eventData.acc_v != null ? Number(eventData.acc_v).toFixed(2) : '-'}</Typography>
              </Grid>
              {showAX && (
                <Grid item xs={4} sm={2} md={2} sx={{ '@media print': { flexBasis: printBasis, maxWidth: printBasis } }}>
                  <Typography variant="caption" color="text.secondary">AX</Typography>
                  <Typography variant="body1" fontWeight="bold">{eventData.ax != null ? Number(eventData.ax).toFixed(2) : '-'}</Typography>
                </Grid>
              )}
              {showAY && (
                <Grid item xs={4} sm={2} md={2} sx={{ '@media print': { flexBasis: printBasis, maxWidth: printBasis } }}>
                  <Typography variant="caption" color="text.secondary">AY</Typography>
                  <Typography variant="body1" fontWeight="bold">{eventData.ay != null ? Number(eventData.ay).toFixed(2) : '-'}</Typography>
                </Grid>
              )}
              {showSVM && (
                <Grid item xs={4} sm={2} md={2} sx={{ '@media print': { flexBasis: printBasis, maxWidth: printBasis } }}>
                  <Typography variant="caption" color="text.secondary">SVM</Typography>
                  <Typography variant="body1" fontWeight="bold">{eventData.svm != null ? Number(eventData.svm).toFixed(2) : '-'}</Typography>
                </Grid>
              )}
              {showTranco && (
                <Grid item xs={4} sm={2} md={2} sx={{ '@media print': { flexBasis: printBasis, maxWidth: printBasis } }}>
                  <Typography variant="caption" color="text.secondary">Tranco</Typography>
                  <Typography variant="body1" fontWeight="bold">{eventData.jerk_h != null ? Number(eventData.jerk_h).toFixed(2) : '-'}</Typography>
                </Grid>
              )}
            </Grid>

            {eventData.lat && eventData.lon && Number(eventData.lat) !== 0 && Number(eventData.lon) !== 0 && (
              <Box mt={3} display="flex" flexDirection="column" alignItems="center">
                <Box sx={{ width: '100%', maxWidth: '800px', position: 'relative', mx: 'auto' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom align="left">Localização</Typography>
                  <Box sx={{
                    position: 'relative',
                    width: '100%',
                    height: '300px',
                    overflow: 'hidden',
                    border: '1px solid #ccc',
                    borderRadius: '8px'
                  }}>
                    <MapContainer
                      center={[Number(eventData.lat), Number(eventData.lon)]}
                      zoom={16}
                      style={{ height: '100%', width: '100%', zIndex: 1 }}
                      scrollWheelZoom={true}
                    >
                      <TileLayer
                        attribution='&copy; OpenStreetMap'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[Number(eventData.lat), Number(eventData.lon)]} icon={customMarkerIcon} />
                    </MapContainer>
                  </Box>
                </Box>
              </Box>
            )}


          </Paper>
        )}

        {!loading && !error && chartOptions && (
          <Box>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Typography variant="h6" color="primary">Métricas do IMU</Typography>
              {fileName && (
                <Typography variant="subtitle2" color="text.secondary" sx={{ opacity: 0.8 }}>
                  — Arquivo: {fileName}
                </Typography>
              )}
            </Box>
            {metrics && (
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Max Mag</Typography>
                    <Typography variant="h6" color="primary.main">{metrics.maxMag.toFixed(2)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Mean Mag</Typography>
                    <Typography variant="h6" color="primary.main">{metrics.meanMag.toFixed(2)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Std Dev</Typography>
                    <Typography variant="h6" color="primary.main">{metrics.stdDev.toFixed(2)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>RMS</Typography>
                    <Typography variant="h6" color="primary.main">{metrics.rms.toFixed(2)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Crest Factor</Typography>
                    <Typography variant="h6" color="primary.main">{metrics.crestFactor.toFixed(2)}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={4} md={2}>
                  <Paper elevation={1} sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>Kurtosis</Typography>
                    <Typography variant="h6" color="primary.main">{metrics.kurtosis.toFixed(2)}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 300 }}>
                  <CanvasJSChart options={chartOptions.acc} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 300 }}>
                  <CanvasJSChart options={chartOptions.telemetry} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 300 }}>
                  <CanvasJSChart options={chartOptions.magHist} />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ height: 300 }}>
                  <CanvasJSChart options={chartOptions.gyro} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {!loading && eventData && (
          <Box mt={4} p={2} sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)', borderLeft: '4px solid #1976d2', borderRadius: '4px' }}>
            <Typography variant="subtitle2" color="primary" gutterBottom fontWeight="bold">
              Como entender os valores de impacto:
            </Typography>
            <Grid container spacing={1}>
              {showAccH && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Acc H (Força Horizontal):</strong> Mostra a força de impactos para os lados, para frente ou para trás. Valores altos indicam batidas ou freadas bruscas.
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Acc V (Força Vertical):</strong> Mostra a força de baixo para cima. Valores altos geralmente indicam que a máquina passou por um buraco ou degrau.
                </Typography>
              </Grid>
              {(showAX || showAY) && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>AX / AY (Direção X e Y):</strong> Detalha a direção exata da força horizontal. Útil para descobrir se a pancada veio de frente, de trás ou da lateral.
                  </Typography>
                </Grid>
              )}
              {showSVM && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>SVM (Força Total):</strong> É a soma de todas as forças sentidas pela máquina. Quanto maior esse número, mais severo foi o impacto geral.
                  </Typography>
                </Grid>
              )}
              {showTranco && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Tranco:</strong> Mede a violência e rapidez do impacto. Um &quot;Tranco&quot; alto significa que a batida foi muito seca e repentina.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ '@media print': { display: 'none' } }}>
        <Button onClick={onClose} color="primary">Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

function createHistogram(data: Float32Array, bins: number) {
  let min = Infinity, max = -Infinity;
  for (let i = 0; i < data.length; i++) {
    if (data[i] < min) min = data[i];
    if (data[i] > max) max = data[i];
  }
  const binSize = (max - min) / bins;
  const freqs = new Array(bins).fill(0);

  for (let i = 0; i < data.length; i++) {
    let idx = Math.floor((data[i] - min) / binSize);
    if (idx === bins) idx--;
    freqs[idx]++;
  }

  const dps = [];
  for (let i = 0; i < bins; i++) {
    const binStart = min + i * binSize;
    const binEnd = min + (i + 1) * binSize;
    dps.push({ label: `${binStart.toFixed(1)} - ${binEnd.toFixed(1)}`, y: freqs[i] });
  }
  return dps;
}
